import React, { memo, useEffect, useState } from 'react';
import { TypographyComponent as TypographyElement } from '@tvg/fuse-design';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import allItemsOutOfStock from 'utilities/sku/isAllOptionsOutOfStock';
import { StockCode } from 'types/ProductResponseType';
import { ProductVariant } from 'types/Product';
import { FspService } from 'services/FSP/fast-selling-product-service';
import { isNotInStock } from 'utilities/util';
import { StockMessageProps } from './StockDataFSPProps';

const extractOptionFromSkus = (data: ProductVariant[]): string[] =>
  data.filter((item) => item.stock && !isNotInStock(item.stock.code)).map((item) => item.option);

export const getStockMessageColour = (code: StockCode): string => {
  switch (code) {
    case StockCode.OUTOFSTOCK:
    case StockCode.UNAVAILABLE:
      return 'var(--color-universal-redMid)';
    case StockCode.DCSTOCK:
    case StockCode.DDSTOCK:
    case StockCode.DDSTOCKWORKINGDAYS:
    case StockCode.DDSTOCKWEEK:
    case StockCode.DDSTOCKDATE:
    case StockCode.DDSTOCKNEXTDATE:
      return 'var(--color-universal-greenMid)';
    default:
      return 'var(--color-universal-bronzeMid)';
  }
};

const StockMessage = memo(
  ({ csrfToken, isFSPEnabled, fspMockEnabled, fspToggle, xMockResponseValue }: StockMessageProps) => {
    const { selectedSku, skus, setSkus } = useSelectedSkuContext();
    const isEachItemOutOfStock = allItemsOutOfStock(skus);
    const [loading, setLoading] = useState(true);

    const FSP = new FspService(fspMockEnabled);

    useEffect(() => {
      if (!(isFSPEnabled && fspToggle)) {
        setLoading(false);
        return;
      }

      const getFSP = async () => {
        try {
          const variantIds = extractOptionFromSkus(skus);

          const response = fspMockEnabled
            ? await FSP.getFSP(csrfToken, variantIds, xMockResponseValue)
            : await FSP.getFSP(csrfToken, variantIds);

          if (response && Array.isArray(response.items)) {
            let hasOutdatedStock = false;

            const updatedSkus = skus.map((sku) => {
              const stockData = response.items.find((item) => item.variantId === sku.option);

              if (stockData?.stockLevel === 0) {
                hasOutdatedStock = true;
                return {
                  ...sku,
                  stock: {
                    code: StockCode.UNAVAILABLE,
                    primary: 'Unavailable',
                    secondary: 'Sorry, this item is currently unavailable',
                  },
                };
              }

              return sku;
            });

            if (hasOutdatedStock) {
              setSkus(updatedSkus);
            }
          }

          setLoading(false);
        } catch (error) {
          console.error('Error fetching FSP data:', error);
        }
      };

      getFSP();
    }, []);

    if (!loading) {
      if (isEachItemOutOfStock) {
        return (
          <div data-testid="stock-message">
            <TypographyElement tag="p" size="1" color={getStockMessageColour(StockCode.OUTOFSTOCK)}>
              {skus[0]?.stock?.primary}
            </TypographyElement>
            <p>{skus[0]?.stock?.secondary}</p>
          </div>
        );
      }

      if (selectedSku?.stock && selectedSku?.stock?.code) {
        return (
          <div data-testid="stock-message">
            <TypographyElement tag="p" size="1" color={getStockMessageColour(selectedSku.stock.code)}>
              {selectedSku?.stock?.primary}
            </TypographyElement>
            <p>{selectedSku?.stock?.secondary}</p>
          </div>
        );
      }
    }

    return null;
  },
);

export default StockMessage;
